import _ from "lodash";
import { useEffect, useCallback, useMemo, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { AppContextStore } from "$stores/app-context";
import { handleAbort } from "$services/api";

export function usePaginatedStoreWrapper(store, options) {
    const { fetchList, reset, setPagingInfo, setPageIndex, setPerPage, setSelectedData, setHiddenColumns, keyPrefix = "tableFilter." } = options;
    const { hasLoadedFilters, globalFilters } = AppContextStore.useState((f) => {
        return { hasLoadedFilters: f.hasLoadedFilters, globalFilters: f.currentFilters, overviewLoading: f.overviewLoading };
    });

    const storeState = store.useState();

    // Ref always holds the latest values so the debounced fn reads fresh state when it fires
    const latestRef = useRef({});
    latestRef.current = { fetchList, globalFilters, storeState, options, keyPrefix };

    // Stable abort controller — no useState so no stale-closure issues
    const controllerRef = useRef(null);

    // Ref tracking previous filters reference for change detection.
    // IMPORTANT: must be initialized to storeState.filters (not null) so that on the
    // first effect run filtersChanged === false, letting isFirstRunRef handle initial load.
    // Initializing to null would make filtersChanged === true on first run and send the
    // initial load request through the debounce path instead of firing immediately.
    const prevFiltersRef = useRef(storeState.filters);

    // Ref tracking whether the initial load has fired
    const isFirstRunRef = useRef(true);

    const [searchParams] = useSearchParams();

    // Stable fetch function — empty deps, reads everything from refs at fire time
    const sendRequest = useCallback(() => {
        const { fetchList, globalFilters, storeState, options, keyPrefix } = latestRef.current;
        if (controllerRef.current) {
            handleAbort([controllerRef.current]);
        }
        const controller = fetchList(
            { ...(options?.suppressGlobalFilters ? { ...(options?.overrideGlobalFilters || {}) } : globalFilters || { ...(options?.overrideGlobalFilters || {}) }) },
            { keyPrefix, ...storeState, ...(options?.overrideFilters ? { filters: { ...(storeState?.filters || {}), ...options?.overrideFilters } } : {}) }
        );
        controllerRef.current = controller;
    }, []); // intentionally empty — sendRequest reads everything from latestRef at call time; listing deps would cause it to recreate and break stability

    // Stable debounced function — created once, never recreated.
    // Uses _.debounce (lodash) which provides .cancel() — the bare 'debounce' package does not.
    // Lazy init so the function is only allocated on first render, not every render.
    const debouncedSendRequestRef = useRef(null);
    if (!debouncedSendRequestRef.current) {
        debouncedSendRequestRef.current = _.debounce(sendRequest, 300);
    }
    const debouncedSendRequest = debouncedSendRequestRef.current;

    // Cleanup: reset store, cancel pending debounce, abort in-flight request on unmount
    useEffect(() => {
        return () => {
            debouncedSendRequest.cancel();
            if (controllerRef.current) {
                handleAbort([controllerRef.current]);
            }
            if (reset) reset();
        };
    }, [reset]); // debouncedSendRequest and controllerRef are stable refs, not reactive values

    // Restore paging state from URL params on initial load
    useEffect(() => {
        if (hasLoadedFilters) {
            const updatables = {};
            const sort = JSON.parse(searchParams.get("sort"));
            const filter = JSON.parse(searchParams.get("filter"));
            const pageIndex = searchParams.get("pageIndex");
            const pageSize = searchParams.get("pageSize");

            if (sort) updatables.sort = sort;
            if (filter) updatables.filter = filter;
            if (pageIndex) updatables.pageIndex = parseInt(pageIndex, 10);
            if (pageSize) updatables.pageSize = parseInt(pageSize, 10);
            if (!_.isEmpty(updatables)) {
                setPagingInfo(updatables);
            }
        }
    }, [hasLoadedFilters, searchParams, setPagingInfo]);

    // Main fetch effect — debounces filter text changes, fires immediately for everything else
    useEffect(() => {
        if (!hasLoadedFilters) return;

        const filtersChanged = prevFiltersRef.current !== storeState.filters;
        prevFiltersRef.current = storeState.filters;

        if (isFirstRunRef.current || !filtersChanged) {
            // Initial load OR sort/pagination/global filter change: fire immediately
            isFirstRunRef.current = false;
            debouncedSendRequest.cancel();
            sendRequest();
        } else {
            // Filter text changed: debounce so rapid keystrokes coalesce into one request
            debouncedSendRequest();
        }
    }, [hasLoadedFilters, globalFilters, storeState.filters, storeState.pageIndex, storeState.pageSize, storeState.sort]); // sendRequest and debouncedSendRequest intentionally omitted — both are stable refs

    return useMemo(
        () => ({
            ...storeState,
            fetchList,
            reset,
            setPagingInfo,
            setPageIndex,
            setPerPage,
            setSelectedData,
            setHiddenColumns,
        }),
        [storeState]
    );
}
